/**
 * The network and overlay views, drawn with Canvas 2D.
 *
 * Both views draw the same three things — links, circles, labels — and differ
 * only in where a circle's colour comes from: its cluster, or its score
 * through a continuous ramp.  Sizes follow VOSviewer exactly:
 *
 * ```
 *   D = floor(scale * max(16 * w^v, 5))      circle diameter, v = 0.5
 *   F = floor(scale * (9 + 4 * w^v))         label font size
 * ```
 *
 * with `w = weight / mean(weight)`.  The square root is the point: it makes
 * *area* roughly proportional to weight, which is how a reader judges a circle.
 * Diameter proportional to weight would make a ten-times-heavier item look a
 * hundred times bigger.
 *
 * Labels are placed by the threshold rule in `../model/labels.ts` and fade in
 * quadratically; nothing here does collision detection per frame.
 */

import type { ColorScheme, RGB, Theme, ViewMode } from '../types';
import type { Scene } from '../model/scene';
import type { OverlayModel } from '../model/overlay';
import type { Viewport } from '../geometry/viewport';
import { css, lightness } from '../color/lab';
import { sampleScheme } from '../color/schemes';
import { labelAlpha } from '../model/labels';
import { CircleSprites } from './sprites';
import {
  CIRCLE_ALPHA,
  CIRCLE_EXTRA_DIAMETER_SELECTED,
  LINE_ALPHA,
  LINE_ALPHA_HIGHLIGHTED,
  SUBLABEL_FONT_SIZE,
} from '../model/constants';

/** Alpha applied to everything that is not the hovered item or one of its neighbours. */
const DIMMED_ALPHA = 0.28;

/** Control-point offset for a link's curve, `ItemLink.calcCurvedLineControlPoint`. */
const CURVE_ALPHA = 0.3;

export interface RenderState {
  mode: ViewMode;
  theme: Theme;
  overlay: OverlayModel | null;
  /** The ramp the overlay was coloured with; the legend re-samples it. */
  overlayScheme: ColorScheme | null;
  /**
   * The colour a density view's field sits on — the scheme's value at zero for
   * item density, the background for cluster density.  Labels take their ink
   * and their halo from it rather than from the page theme, because a density
   * map's ground has nothing to do with whether the page is light or dark.
   */
  densityGround: RGB | null;
  /** Item index under the pointer, or -1. */
  hovered: number;
  /** Selected item index, or -1. */
  selected: number;
  /** Keyboard cursor item index, or -1. */
  focused: number;
  minLinkStrength: number;
  maxLines: number;
  showLegend: boolean;
  showTooltip: boolean;
  /** True when the canvas itself has keyboard focus, so the focus ring is drawn. */
  canvasFocused: boolean;
  fontFamily: string;
  scale: number;
}

export interface DrawResult {
  /** Number of labels drawn at full opacity — reported through `viewchange`. */
  visibleLabels: number;
  /** Number of link lines actually drawn. */
  drawnLinks: number;
}

export function itemColor(scene: Scene, state: RenderState, i: number): RGB {
  if (state.mode === 'overlay' && state.overlay) {
    const rgb = state.overlay.rgb;
    return [rgb[i * 3] as number, rgb[i * 3 + 1] as number, rgb[i * 3 + 2] as number];
  }
  const rgb = scene.clusterRgb;
  return [rgb[i * 3] as number, rgb[i * 3 + 1] as number, rgb[i * 3 + 2] as number];
}

/**
 * Indices of the links that pass the strength and count filters.
 *
 * `scene.linkStrength` is sorted descending, so the count cap is a prefix and
 * the strength filter a break — no scan over a million links to draw a
 * thousand.
 */
export function visibleLinkCount(scene: Scene, minStrength: number, maxLines: number): number {
  const cap = Math.min(scene.linkStrength.length, Math.max(0, Math.floor(maxLines)));
  let k = 0;
  while (k < cap && (scene.linkStrength[k] as number) >= minStrength) k += 1;
  return k;
}

/** Items linked to `index` among the currently visible links. */
export function neighboursOf(scene: Scene, index: number, linkCount: number): Set<number> {
  const set = new Set<number>();
  if (index < 0) return set;
  for (let k = 0; k < linkCount; k += 1) {
    const s = scene.linkSource[k] as number;
    const t = scene.linkTarget[k] as number;
    if (s === index) set.add(t);
    else if (t === index) set.add(s);
  }
  return set;
}

function drawLink(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  scene: Scene,
  k: number,
  width: number,
): void {
  const s = scene.linkSource[k] as number;
  const t = scene.linkTarget[k] as number;
  const x1 = scene.x[s] as number;
  const y1 = scene.y[s] as number;
  const x2 = scene.x[t] as number;
  const y2 = scene.y[t] as number;
  // VOSviewer bows alternate links to opposite sides so that two links between
  // the same neighbourhood stay distinguishable instead of overprinting.
  const sign = k % 2 === 0 ? 1 : -1;
  const cx = (x1 + x2) / 2 + sign * CURVE_ALPHA * (y2 - y1);
  const cy = (y1 + y2) / 2 - sign * CURVE_ALPHA * (x2 - x1);
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(vp.toScreenX(x1), vp.toScreenY(y1));
  ctx.quadraticCurveTo(vp.toScreenX(cx), vp.toScreenY(cy), vp.toScreenX(x2), vp.toScreenY(y2));
  ctx.stroke();
}

function drawLinks(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  linkCount: number,
  highlight: number,
): void {
  ctx.lineCap = 'round';
  const focusOnly = highlight >= 0;
  // Pass one: everything unrelated to the highlighted item, dimmed when there
  // is one. Colour is taken from the source endpoint, lightened, as VOSviewer
  // does for its coloured lines.
  ctx.globalAlpha = focusOnly ? DIMMED_ALPHA : 1;
  for (let k = 0; k < linkCount; k += 1) {
    const s = scene.linkSource[k] as number;
    const t = scene.linkTarget[k] as number;
    if (focusOnly && (s === highlight || t === highlight)) continue;
    const c = itemColor(scene, state, s);
    ctx.strokeStyle = css(c, LINE_ALPHA);
    drawLink(ctx, vp, scene, k, scene.linkWidth[k] as number);
  }
  if (focusOnly) {
    ctx.globalAlpha = 1;
    for (let k = 0; k < linkCount; k += 1) {
      const s = scene.linkSource[k] as number;
      const t = scene.linkTarget[k] as number;
      if (s !== highlight && t !== highlight) continue;
      const c = itemColor(scene, state, s === highlight ? t : s);
      ctx.strokeStyle = css(c, LINE_ALPHA_HIGHLIGHTED);
      drawLink(ctx, vp, scene, k, (scene.linkWidth[k] as number) + 1);
    }
  }
  ctx.globalAlpha = 1;
}

function drawCircles(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  sprites: CircleSprites,
  highlight: number,
  neighbours: Set<number>,
): void {
  const [vxMin, vyMin, vxMax, vyMax] = vp.visibleBounds();
  // Cull with a generous margin: an item just off-screen can still have a
  // circle whose edge reaches in.
  const marginWorld = 64 / vp.scale;
  const order = scene.order;
  const dimming = highlight >= 0;
  // Lightest first so the heaviest circles end up on top, matching VOSviewer's
  // reverse iteration over its weight-sorted list.
  for (let rank = order.length - 1; rank >= 0; rank -= 1) {
    const i = order[rank] as number;
    const wx = scene.x[i] as number;
    const wy = scene.y[i] as number;
    if (
      wx < vxMin - marginWorld ||
      wx > vxMax + marginWorld ||
      wy < vyMin - marginWorld ||
      wy > vyMax + marginWorld
    ) {
      continue;
    }
    const emphasised = !dimming || i === highlight || neighbours.has(i);
    ctx.globalAlpha = emphasised ? 1 : DIMMED_ALPHA;
    const selected = i === state.selected || i === highlight;
    const d = (scene.diameter[i] as number) + (selected ? CIRCLE_EXTRA_DIAMETER_SELECTED : 0);
    const sprite = sprites.get(itemColor(scene, state, i), selected ? 1 : CIRCLE_ALPHA);
    if (!sprite) continue;
    const cx = vp.toScreenX(wx);
    const cy = vp.toScreenY(wy);
    ctx.drawImage(sprite, cx - d / 2, cy - d / 2, d, d);
  }
  ctx.globalAlpha = 1;
}

function drawLabels(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  highlight: number,
  neighbours: Set<number>,
  onDensity: boolean,
): number {
  const order = scene.order;
  const [vxMin, vyMin, vxMax, vyMax] = vp.visibleBounds();
  const marginWorld = 200 / vp.scale;
  // VOSviewer flips its density labels to white when the ground is black. The
  // same decision, generalised: measure the ground's L* and pick the ink that
  // survives on it, which also handles the white-* schemes where the field
  // starts light and black labels are correct.
  const ground = onDensity ? state.densityGround : null;
  const labelRgb: RGB = ground
    ? lightness(ground) > 55
      ? [0, 0, 0]
      : [255, 255, 255]
    : state.theme.label;
  const haloRgb: RGB = ground ?? state.theme.background;
  const subLabelRgb: RGB = ground
    ? lightness(ground) > 55
      ? [70, 70, 76]
      : [206, 208, 218]
    : state.theme.mutedLabel;
  const subSize = Math.max(1, Math.floor(state.scale * SUBLABEL_FONT_SIZE));
  const dimming = highlight >= 0;
  let visible = 0;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  for (let rank = order.length - 1; rank >= 0; rank -= 1) {
    const i = order[rank] as number;
    const wx = scene.x[i] as number;
    const wy = scene.y[i] as number;
    if (
      wx < vxMin - marginWorld ||
      wx > vxMax + marginWorld ||
      wy < vyMin - marginWorld ||
      wy > vyMax + marginWorld
    ) {
      continue;
    }
    const forced = i === highlight || i === state.selected || i === state.focused;
    let alpha = forced ? 1 : labelAlpha(scene.labelThreshold[i] as number, vp.scale);
    if (alpha <= 0) continue;
    if (alpha >= 0.9) visible += 1;
    if (dimming && !forced && !neighbours.has(i)) alpha *= DIMMED_ALPHA;

    const cx = vp.toScreenX(wx);
    const cy = vp.toScreenY(wy);
    const mainH = scene.mainH[i] as number;
    const subH = scene.subH[i] as number;
    const totalH = mainH + subH;
    ctx.fillStyle = css(labelRgb, alpha);
    // On a density view VOSviewer draws a plain label with no halo; on a
    // network view the label sits over its own circle. A thin stroke in the
    // background colour keeps it legible over both without changing its hue.
    ctx.font = `${Math.max(1, scene.fontSize[i] as number)}px ${state.fontFamily}`;
    const mainY = cy - totalH / 2 + 0.95 * (scene.mainAscent[i] as number);
    if (onDensity) {
      // A thin halo in the ground colour, scaled with the font so a 40-pixel
      // label does not get a hairline and a 9-pixel one does not get a blob.
      ctx.lineWidth = Math.max(2, (scene.fontSize[i] as number) * 0.16);
      ctx.lineJoin = 'round';
      ctx.strokeStyle = css(haloRgb, alpha * 0.7);
      ctx.strokeText(scene.labelText[i] as string, cx, mainY);
    }
    ctx.fillText(scene.labelText[i] as string, cx, mainY);
    const sub = scene.sublabelText[i] as string;
    if (sub !== '') {
      ctx.font = `${subSize}px ${state.fontFamily}`;
      ctx.fillStyle = css(subLabelRgb, alpha);
      const subY = cy - totalH / 2 + mainH + 0.85 * (scene.subAscent[i] as number);
      if (onDensity) ctx.strokeText(sub, cx, subY);
      ctx.fillText(sub, cx, subY);
    }
  }
  return visible;
}

/**
 * The overlay colour bar.
 *
 * Ticks come from VOSviewer's own algorithm, so the bar reads 2016, 2018, 2020
 * rather than 2015.7, 2018.4, 2021.1.
 */
function drawLegend(ctx: CanvasRenderingContext2D, state: RenderState, width: number, height: number): void {
  const overlay = state.overlay;
  if (!overlay || state.mode !== 'overlay') return;
  const barWidth = Math.min(220, Math.max(120, width * 0.24));
  const barHeight = 12;
  const pad = 12;
  const labelHeight = 14;
  const x = width - barWidth - pad;
  const y = height - pad - labelHeight - barHeight;

  // The bar is sampled from the same scheme, through the same Lab
  // interpolation and the same clamping as the items, so it cannot drift from
  // the map it explains.
  const scheme = state.overlayScheme;
  const gradient = ctx.createLinearGradient(x, 0, x + barWidth, 0);
  const steps = 32;
  for (let s = 0; s <= steps; s += 1) {
    const t = s / steps;
    gradient.addColorStop(t, css(scheme ? sampleScheme(scheme, t) : state.theme.noValue));
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, barWidth, barHeight);
  ctx.strokeStyle = css(state.theme.legendFrame);
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, barWidth - 1, barHeight - 1);

  ctx.font = `11px ${state.fontFamily}`;
  ctx.fillStyle = css(state.theme.legendText);
  ctx.textBaseline = 'top';
  const span = overlay.max - overlay.min;
  for (let t = 0; t < overlay.ticks.values.length; t += 1) {
    const value = overlay.ticks.values[t] as number;
    if (span <= 0) continue;
    const frac = (value - overlay.min) / span;
    if (frac < -1e-9 || frac > 1 + 1e-9) continue;
    const tx = x + frac * barWidth;
    ctx.textAlign = frac <= 0.02 ? 'left' : frac >= 0.98 ? 'right' : 'center';
    ctx.fillText(overlay.ticks.labels[t] as string, tx, y + barHeight + 2);
  }
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(overlay.field, x + barWidth, y - 3);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawTooltip(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  index: number,
  width: number,
  height: number,
): void {
  if (index < 0 || !state.showTooltip) return;
  const item = scene.doc.items[index];
  if (!item) return;

  const lines: string[] = [];
  lines.push(item.label);
  if (item.sublabel) lines.push(item.sublabel);
  const c = scene.cluster[index] as number;
  const weightText = formatNumber(item.weight);
  lines.push(c >= 0 ? `cluster ${c + 1} · weight ${weightText}` : `weight ${weightText}`);
  if (state.mode === 'overlay' && state.overlay) {
    const v = state.overlay.score[index] as number;
    lines.push(`${state.overlay.field}: ${Number.isNaN(v) ? 'no value' : formatNumber(v)}`);
  }
  if (item.description) lines.push(truncate(item.description, 90));

  const titleFont = `600 12px ${state.fontFamily}`;
  const bodyFont = `11px ${state.fontFamily}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let boxWidth = 0;
  for (let i = 0; i < lines.length; i += 1) {
    ctx.font = i === 0 ? titleFont : bodyFont;
    boxWidth = Math.max(boxWidth, ctx.measureText(lines[i] as string).width);
  }
  const padding = 8;
  const lineHeight = 15;
  const boxHeight = lines.length * lineHeight + padding * 2 - 3;
  boxWidth += padding * 2;

  const anchorX = vp.toScreenX(scene.x[index] as number);
  const anchorY = vp.toScreenY(scene.y[index] as number);
  const offset = (scene.diameter[index] as number) / 2 + 8;
  let bx = anchorX + offset;
  let by = anchorY - boxHeight / 2;
  if (bx + boxWidth > width - 4) bx = anchorX - offset - boxWidth;
  if (bx < 4) bx = 4;
  if (by < 4) by = 4;
  if (by + boxHeight > height - 4) by = height - 4 - boxHeight;

  ctx.fillStyle = css(state.theme.tooltipBackground, 0.97);
  ctx.strokeStyle = css(state.theme.tooltipBorder);
  ctx.lineWidth = 1;
  roundRect(ctx, bx + 0.5, by + 0.5, boxWidth, boxHeight, 6);
  ctx.fill();
  ctx.stroke();

  for (let i = 0; i < lines.length; i += 1) {
    ctx.font = i === 0 ? titleFont : bodyFont;
    ctx.fillStyle = css(i === 0 ? state.theme.tooltipText : state.theme.mutedLabel);
    ctx.fillText(lines[i] as string, bx + padding, by + padding + i * lineHeight);
  }
}

function formatNumber(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(Math.abs(v) >= 100 ? 1 : 2);
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * A focus ring inside the canvas edge, plus a marker on the keyboard cursor.
 *
 * A canvas cannot show a CSS focus ring on the item the user is "on", because
 * the items are not elements.  Drawing both makes keyboard use legible: the
 * ring says the canvas has focus, the marker says which item the arrow keys
 * and Enter will act on.
 */
function drawFocus(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  width: number,
  height: number,
): void {
  if (state.canvasFocused) {
    ctx.strokeStyle = css(state.theme.focusRing);
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.strokeRect(1, 1, width - 2, height - 2);
  }
  if (state.focused >= 0 && state.focused < scene.n) {
    const cx = vp.toScreenX(scene.x[state.focused] as number);
    const cy = vp.toScreenY(scene.y[state.focused] as number);
    const r = (scene.diameter[state.focused] as number) / 2 + 5;
    ctx.strokeStyle = css(state.theme.focusRing);
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

/**
 * Screen-space chrome: the colour bar, the focus ring, the tooltip.
 *
 * Split out from `drawFrame` because it must *not* go into the pan buffer.
 * That buffer is blitted at an offset while the pointer drags, and a legend
 * that slid off the corner with the map would give the game away.
 */
export function drawChrome(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  width: number,
  height: number,
): void {
  if (state.showLegend) drawLegend(ctx, state, width, height);
  drawFocus(ctx, scene, vp, state, width, height);
  drawTooltip(ctx, scene, vp, state, state.hovered, width, height);
}

/**
 * Draw one frame's map content.
 *
 * For the two density modes the caller has already painted the field, and this
 * adds only labels on top of it — which is what VOSviewer does: its density
 * views draw labels, no circles and no links.
 *
 * Pass `chrome: false` when rendering into the pan buffer.
 */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  state: RenderState,
  sprites: CircleSprites,
  width: number,
  height: number,
  chrome = true,
): DrawResult {
  const isDensity = state.mode === 'item-density' || state.mode === 'cluster-density';
  const highlight = state.hovered >= 0 ? state.hovered : state.selected;
  let drawnLinks = 0;
  let visibleLabels: number;

  if (isDensity) {
    visibleLabels = drawLabels(ctx, scene, vp, state, highlight, new Set<number>(), true);
  } else {
    drawnLinks = visibleLinkCount(scene, state.minLinkStrength, state.maxLines);
    const neighbours = neighboursOf(scene, highlight, drawnLinks);
    drawLinks(ctx, scene, vp, state, drawnLinks, highlight);
    drawCircles(ctx, scene, vp, state, sprites, highlight, neighbours);
    visibleLabels = drawLabels(ctx, scene, vp, state, highlight, neighbours, false);
  }
  if (chrome) drawChrome(ctx, scene, vp, state, width, height);
  return { visibleLabels, drawnLinks };
}
